import { type LibraryWithPermissions } from '@air/api/types';

export type ReorderLibrariesByIdsParams = {
  libraries: LibraryWithPermissions[];
  idsToMove: string[];
  betweenIds: {
    moveBeforeId?: string;
    moveAfterId?: string;
  };
};

/**
 * Reorders libraries based on IDs to move and target position IDs.
 * Used for optimistic updates when repositioning libraries via drag-and-drop.
 *
 * The function removes the libraries to move from the array, finds the insertion
 * point based on moveBeforeId or moveAfterId, and inserts them at that position.
 *
 * @param libraries - The current array of libraries
 * @param idsToMove - Array of library IDs to reposition
 * @param betweenIds - Target position IDs (moveBeforeId or moveAfterId)
 * @returns The reordered array of libraries
 *
 * @example
 * ```ts
 * const reordered = reorderLibrariesByIds({
 *   libraries: [lib1, lib2, lib3],
 *   idsToMove: ['lib1-id'],
 *   betweenIds: { moveAfterId: 'lib3-id' }
 * });
 * // Result: [lib2, lib3, lib1]
 * ```
 */
export const reorderLibrariesByIds = ({
  libraries,
  idsToMove,
  betweenIds,
}: ReorderLibrariesByIdsParams): LibraryWithPermissions[] => {
  const librariesToMove = libraries.filter((lib) => idsToMove.includes(lib.id));
  const reorderedData = libraries.filter((lib) => !idsToMove.includes(lib.id));

  // Find insertion index based on target position
  // If moveAfterId is specified, insert after that library
  // If moveBeforeId is specified, insert before that library
  // If neither is specified, insert at the beginning
  let insertIndex = 0;
  if (betweenIds.moveAfterId) {
    const afterIndex = reorderedData.findIndex((lib) => lib.id === betweenIds.moveAfterId);
    insertIndex = afterIndex !== -1 ? afterIndex + 1 : reorderedData.length;
  } else if (betweenIds.moveBeforeId) {
    const beforeIndex = reorderedData.findIndex((lib) => lib.id === betweenIds.moveBeforeId);
    insertIndex = beforeIndex !== -1 ? beforeIndex : 0;
  }

  reorderedData.splice(insertIndex, 0, ...librariesToMove);

  return reorderedData;
};
